import { handlePedalEvent, releaseAll } from "@app/lib/pedal/transportControls.js";
import {
    createWebHidAdapter,
    listHidDevices,
    requestHidDevice,
} from "@app/lib/pedal/adapters/webHidAdapter.js";
import { createVPedalBrowserAdapter } from "@app/lib/pedal/adapters/vPedalBrowserAdapter.js";
import {
    createGamepadAdapter,
    listGamepads,
} from "@app/lib/pedal/adapters/gamepadAdapter.js";

export const PEDAL_SOURCES = {
    WEBHID: "webhid",
    VPEDAL: "vpedal",
    GAMEPAD: "gamepad",
};

const STORAGE_KEY = "cc-device-defaults";

const DEFAULT_FUNCTION_MAPPING = {
    0: "pause",
    1: "fastForward",
    2: "play",
    3: "none",
    4: "rewind",
};

/**
 * Philips Footcontrol USB (Vendor 0911, Product 1844 / 091a, etc.)
 * exposes three independent pedals. Matches reported behavior:
 * buttons[0] -> fastForward, buttons[1] -> play, buttons[2] -> rewind.
 */
const DEFAULT_GAMEPAD_BUTTON_MAPPING = {
    0: "fastForward",
    1: "play",
    2: "rewind",
    3: "none",
};

const PHILIPS_FOOTCONTROL_IDS = ["0911-1844", "0911-091a"];

export const pedalState = $state({
    enable: false,
    /** @type {'webhid' | 'vpedal' | 'gamepad'} */
    source: PEDAL_SOURCES.WEBHID,
    /** @type {HIDDevice | undefined} */
    selectedInput: undefined,
    /** @type {HIDDevice[]} */
    inputDevices: [],
    /** @type {{ index: number, id: string }[]} */
    gamepads: [],
    /** @type {number | null} */
    selectedGamepadIndex: null,
    functionMapping: { ...DEFAULT_FUNCTION_MAPPING },
    /** @type {'independent' | 'combo'} */
    gamepadMode: "independent",
    gamepadButtonMapping: { ...DEFAULT_GAMEPAD_BUTTON_MAPPING },
    /** @type {string} */
    deviceName: "",
    /** Last pedal event for optional UI debug */
    lastEvent: null,
    /** Whether an adapter is actively listening */
    running: false,
    error: "",
});

/** @type {{ start: () => Promise<void>, stop: () => Promise<void> } | null} */
let activeAdapter = null;

function onPedalEvent(event) {
    pedalState.lastEvent = {
        ...event,
        at: Date.now(),
    };
    handlePedalEvent(event);
}

function getPersistedSettings() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (err) {
        console.error("Error reading pedal settings:", err);
        return null;
    }
}

/**
 * Persist current pedal preferences.
 */
export function savePedalSettings() {
    const payload = {
        enable: pedalState.enable,
        source: pedalState.source,
        deviceName: pedalState.selectedInput?.productName || pedalState.deviceName || "",
        selectedGamepadIndex: pedalState.selectedGamepadIndex,
        functionMapping: { ...pedalState.functionMapping },
        gamepadMode: pedalState.gamepadMode,
        gamepadButtonMapping: { ...pedalState.gamepadButtonMapping },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

/**
 * Migrate / load saved settings into state (does not start adapters).
 */
export function loadSavedSettings() {
    const settings = getPersistedSettings();
    if (!settings) return;

    pedalState.enable = Boolean(settings.enable);
    pedalState.source = settings.source || PEDAL_SOURCES.WEBHID;
    pedalState.deviceName = settings.deviceName || "";
    pedalState.selectedGamepadIndex =
        typeof settings.selectedGamepadIndex === "number"
            ? settings.selectedGamepadIndex
            : null;
    pedalState.functionMapping = {
        ...DEFAULT_FUNCTION_MAPPING,
        ...(settings.functionMapping || {}),
    };
    pedalState.gamepadMode = settings.gamepadMode || "independent";
    pedalState.gamepadButtonMapping = {
        ...DEFAULT_GAMEPAD_BUTTON_MAPPING,
        ...(settings.gamepadButtonMapping || {}),
    };
}

async function stopActiveAdapter() {
    if (activeAdapter) {
        try {
            await activeAdapter.stop();
        } catch (err) {
            console.warn("[pedalStore] adapter stop failed", err);
        }
        activeAdapter = null;
    }
    pedalState.running = false;
    await releaseAll();
}

async function startWebHidAdapter() {
    await refreshHidDevices();

    let device = pedalState.selectedInput;
    if (!device && pedalState.deviceName) {
        device = pedalState.inputDevices.find(
            (d) => d.productName === pedalState.deviceName,
        );
    }
    if (!device && pedalState.inputDevices.length > 0) {
        device = pedalState.inputDevices[0];
    }
    if (!device) {
        throw new Error("No WebHID foot pedal device available. Grant access in Options.");
    }

    pedalState.selectedInput = device;
    pedalState.deviceName = device.productName || "";

    activeAdapter = createWebHidAdapter({
        device,
        getMapping: () => pedalState.functionMapping,
        onPedalEvent,
    });
    await activeAdapter.start();
}

async function startVPedalAdapter() {
    activeAdapter = createVPedalBrowserAdapter({ onPedalEvent });
    await activeAdapter.start();
}

async function startGamepadAdapter() {
    refreshGamepads();
    activeAdapter = createGamepadAdapter({
        onPedalEvent,
        getGamepadIndex: () => pedalState.selectedGamepadIndex,
        getMode: () => pedalState.gamepadMode,
        getMapping: () => pedalState.gamepadButtonMapping,
    });
    await activeAdapter.start();
}

/**
 * Start the adapter for the current source.
 */
export async function startPedalControl() {
    pedalState.error = "";
    await stopActiveAdapter();

    if (!pedalState.enable) {
        return;
    }

    try {
        if (pedalState.source === PEDAL_SOURCES.WEBHID) {
            await startWebHidAdapter();
        } else if (pedalState.source === PEDAL_SOURCES.VPEDAL) {
            await startVPedalAdapter();
        } else if (pedalState.source === PEDAL_SOURCES.GAMEPAD) {
            await startGamepadAdapter();
        } else {
            throw new Error(`Unknown pedal source: ${pedalState.source}`);
        }
        pedalState.running = true;
    } catch (err) {
        pedalState.error = err?.message || String(err);
        pedalState.running = false;
        console.error("[pedalStore] failed to start", err);
        throw err;
    }
}

/**
 * Stop listening and optionally disable.
 * @param {{ disable?: boolean }} [opts]
 */
export async function stopPedalControl(opts = {}) {
    await stopActiveAdapter();
    if (opts.disable) {
        pedalState.enable = false;
        savePedalSettings();
    }
}

/**
 * Enable/disable pedal control and start/stop the active adapter.
 * For WebHID, pass `{ requestPermission: true }` from a user-gesture handler
 * when no devices are granted yet.
 * @param {boolean} [enable]
 * @param {{ requestPermission?: boolean }} [opts]
 */
export async function togglePedalControl(enable, opts = {}) {
    const next = enable ?? !pedalState.enable;
    pedalState.enable = next;
    pedalState.error = "";

    if (!next) {
        await stopActiveAdapter();
        pedalState.inputDevices = [];
        pedalState.selectedInput = undefined;
        savePedalSettings();
        return;
    }

    if (pedalState.source === PEDAL_SOURCES.WEBHID) {
        await refreshHidDevices();
        const needsPicker =
            opts.requestPermission === true ||
            (opts.requestPermission !== false && pedalState.inputDevices.length === 0);

        if (needsPicker) {
            try {
                const requested = await requestHidDevice();
                if (requested?.length) {
                    await refreshHidDevices();
                    pedalState.selectedInput = requested[0];
                    pedalState.deviceName = requested[0].productName || "";
                }
            } catch (err) {
                // User cancelled picker — still try previously granted devices
                console.log("[pedalStore] HID request:", err?.message || err);
            }
        } else if (pedalState.deviceName) {
            const match = pedalState.inputDevices.find(
                (d) => d.productName === pedalState.deviceName,
            );
            if (match) {
                pedalState.selectedInput = match;
            }
        }
    }

    if (pedalState.source === PEDAL_SOURCES.GAMEPAD) {
        refreshGamepads();
    }

    savePedalSettings();
    await startPedalControl();
}

export async function refreshHidDevices() {
    try {
        pedalState.inputDevices = await listHidDevices();
    } catch (err) {
        console.error("[pedalStore] list HID failed", err);
        pedalState.inputDevices = [];
    }
}

export function refreshGamepads() {
    pedalState.gamepads = listGamepads();
    if (
        pedalState.selectedGamepadIndex != null &&
        !pedalState.gamepads.some((g) => g.index === pedalState.selectedGamepadIndex)
    ) {
        pedalState.selectedGamepadIndex =
            pedalState.gamepads[0]?.index ?? null;
    } else if (pedalState.selectedGamepadIndex == null && pedalState.gamepads.length) {
        pedalState.selectedGamepadIndex = pedalState.gamepads[0].index;
    }
    applyGamepadPreset();
}

function normalizeGamepadId(id) {
    return (id || "").toLowerCase().replace(/0x/g, "").replace(/:/g, "-").trim();
}

/**
 * Auto-apply Philips Footcontrol preset when a matching device is selected.
 * Only applies defaults while the mapping is still at built-in defaults
 * so user customizations are preserved.
 */
function applyGamepadPreset() {
    const pad = pedalState.gamepads.find(
        (g) => g.index === pedalState.selectedGamepadIndex,
    );
    if (!pad) return;

    const id = normalizeGamepadId(pad.id);
    const isPhilips = PHILIPS_FOOTCONTROL_IDS.some((pid) => id.includes(pid));
    if (!isPhilips) return;

    const hasCustomMapping =
        pedalState.gamepadMode !== "independent" ||
        Object.entries(pedalState.gamepadButtonMapping).some(
            ([k, v]) => DEFAULT_GAMEPAD_BUTTON_MAPPING[k] !== v,
        );

    if (!hasCustomMapping) {
        pedalState.gamepadMode = "independent";
        pedalState.gamepadButtonMapping = { ...DEFAULT_GAMEPAD_BUTTON_MAPPING };
        savePedalSettings();
    }
}

/**
 * @param {string} name
 */
export async function selectDeviceByName(name) {
    const index = pedalState.inputDevices.findIndex((d) => d.productName === name);
    await selectDeviceByIndex(index);
}

/**
 * @param {number} index
 */
export async function selectDeviceByIndex(index) {
    if (index < 0 || index >= pedalState.inputDevices.length) {
        pedalState.selectedInput = undefined;
        pedalState.deviceName = "";
        savePedalSettings();
        return;
    }

    pedalState.selectedInput = pedalState.inputDevices[index];
    pedalState.deviceName = pedalState.selectedInput.productName || "";
    savePedalSettings();

    if (pedalState.enable && pedalState.source === PEDAL_SOURCES.WEBHID) {
        await startPedalControl();
    }
}

/**
 * @param {'webhid' | 'vpedal' | 'gamepad'} source
 */
export async function setPedalSource(source) {
    if (pedalState.source === source) return;
    pedalState.source = source;
    pedalState.error = "";
    savePedalSettings();

    if (pedalState.enable) {
        if (source === PEDAL_SOURCES.WEBHID) {
            await togglePedalControl(true, { requestPermission: true });
        } else {
            await startPedalControl();
        }
    }
}

/**
 * @param {number | null} index
 */
export async function setSelectedGamepadIndex(index) {
    pedalState.selectedGamepadIndex = index;
    applyGamepadPreset();
    savePedalSettings();
    if (pedalState.enable && pedalState.source === PEDAL_SOURCES.GAMEPAD) {
        await startPedalControl();
    }
}

/**
 * @param {'independent' | 'combo'} mode
 */
export async function setGamepadMode(mode) {
    pedalState.gamepadMode = mode;
    savePedalSettings();
    if (pedalState.enable && pedalState.source === PEDAL_SOURCES.GAMEPAD) {
        await startPedalControl();
    }
}

/**
 * @param {number | string} buttonIndex
 * @param {string} action
 */
export async function setGamepadButtonMapping(buttonIndex, action) {
    pedalState.gamepadButtonMapping[buttonIndex] = action;
    savePedalSettings();
}

/**
 * Update a single HID report-value mapping and persist.
 * @param {number | string} value
 * @param {string} action
 */
export function setFunctionMapping(value, action) {
    pedalState.functionMapping[value] = action;
    savePedalSettings();
}

// Load preferences on module init (adapters started from Creator / Options)
loadSavedSettings();
